import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { addLanguagePrefix, type SupportedLanguage } from "@/lib/i18n";

// Original fallback
import heroBackground from "@/assets/hero-aerial-house-truck.jpg";
// WebP responsive variants for better performance
import heroWebpSmall from "@/assets/hero-aerial-house-truck.jpg?w=800&format=webp";
import heroWebpMedium from "@/assets/hero-aerial-house-truck.jpg?w=1200&format=webp";
import heroWebpLarge from "@/assets/hero-aerial-house-truck.jpg?w=1920&format=webp";

const HeroSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('home');
  const { t: tCommon } = useTranslation('common');
  const currentLang = i18n.language as SupportedLanguage;

  // Chalk line snaps in once mounted (the one signature, brand red).
  const [chalkVisible, setChalkVisible] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setChalkVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const navigateTo = (path: string) => {
    const langPath = addLanguagePrefix(path, currentLang);
    navigate(langPath);
  };

  // Build srcset for responsive images
  const heroSrcSet = `${heroWebpSmall} 800w, ${heroWebpMedium} 1200w, ${heroWebpLarge} 1920w`;

  return (
    <>
      <Helmet>
        {/* Preload the LCP image with high priority */}
        <link
          rel="preload"
          as="image"
          href={heroWebpLarge}
          imageSrcSet={heroSrcSet}
          imageSizes="100vw"
          type="image/webp"
        />
      </Helmet>
      <section className="on-navy relative flex min-h-[90vh] w-full items-center overflow-hidden bg-navy">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <picture>
            <source
              type="image/webp"
              srcSet={heroSrcSet}
              sizes="100vw"
            />
            <img
              src={heroBackground}
              alt="Altum Contractors Houston roofing team installing a new residential roof"
              className="absolute inset-0 h-full w-full object-cover object-center"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          {/* Navy scrim: legibility only */}
          <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-transparent to-transparent" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-40 pb-20 sm:px-6 lg:px-8 lg:pt-44">
          <div className="max-w-4xl">
            {/* Chalk line: the one signature, brand red */}
            <div className="mb-6 flex items-center">
              <span
                aria-hidden="true"
                className={`chalk-line chalk-line--snap${chalkVisible ? " is-visible" : ""}`}
              />
            </div>

            {/* Main Headline */}
            <motion.h1
              className="mb-8 font-heading text-5xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-8xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              {t('hero.headlinePart1')} <br />
              {t('hero.headlinePart2')}
              <br />
              <span className="text-white">{t('hero.headlinePart3')}</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              className="mb-10 max-w-2xl text-xl leading-relaxed text-on-navy sm:text-2xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              {t('hero.subheadline')}
            </motion.p>

            {/* CTA + micro-copy */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <Button
                variant="cta"
                size="xl"
                data-primary-cta
                onClick={() => {
                  const w = window as unknown as { fbq?: (...args: unknown[]) => void };
                  if (typeof window !== 'undefined' && w.fbq) {
                    w.fbq('track', 'Contact', {
                      content_name: 'Get Free Inspection Button - Hero'
                    });
                  }
                  navigateTo("/book-inspection");
                }}
              >
                {tCommon('cta.getFreeInspection')}
              </Button>

              <p className="mt-6 text-sm text-on-navy-small">
                {t('hero.microcopy')}
              </p>

              {/* Trust strip: the proof, in one line */}
              <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/15 pt-6 text-sm font-semibold text-white">
                {[
                  tCommon('trustBadges.roofsCompleted'),
                  `25+ ${tCommon('trustBadges.yearsExperience')}`,
                  tCommon('trustBadges.owensCorning'),
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" strokeWidth={3} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
